/* eslint-disable no-console */
import axios from 'axios';
import qs from 'qs';
import * as API_PATHS from '../constants/api';
import { AI_REVERSER_GATEWAY_BASE_URL } from '../constants/api.ar';
import { FRONTEND_SERVICE_BASE_URL } from '../constants/api.fes';
import { TENANT_MGMT_SERVICE_BASE_URL } from '../constants/api-tms';
import { isEmbedded } from '../utilities';

export const PARENT_REF_HEADER = 'x-parent-referrer';

export const getDocumentReferrer = () =>
  document.referrer ||
  qs.parse(decodeURIComponent(window.location.search), { ignoreQueryPrefix: true })[PARENT_REF_HEADER];

const setDefHeaders = () => {
  if (isEmbedded) {
    const referrer = getDocumentReferrer();
    referrer && (axios.defaults.headers.common[PARENT_REF_HEADER] = referrer);
  }
};
setDefHeaders();

export const defaultClient = axios.create({
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

export const noCacheClient = axios.create({ headers: { 'Cache-Control': 'no-cache' } });

export const authClient = axios.create({
  headers: { 'Content-Type': 'application/json', Cookie: '' },
  withCredentials: false
});

export const fesClient = axios.create({
  baseURL: FRONTEND_SERVICE_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

export const tmsClient = axios.create({
  baseURL: TENANT_MGMT_SERVICE_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

export const temClient = axios.create({
  baseURL: API_PATHS.TEM_SERVICE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

export const arClient = axios.create({
  baseURL: AI_REVERSER_GATEWAY_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

const REGIONS = ['us', 'eu', 'au'];

export const iocDashboardClient = axios.create({
  baseURL: API_PATHS.IOC_INTEL_DASHBOARD_SERVICE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

const unauthorizedHandlers = [];

/**
 * Registers a handler invoked with every response error from the clients created here
 * (authClient excluded - login failures must not count as session errors).
 * Used by the shell's unauthorized-interceptor to detect dead sessions. Handlers must
 * not throw and must not swallow the error - the original rejection is always propagated.
 */
export const registerUnauthorizedHandler = handler => unauthorizedHandlers.push(handler);

/**
 * Wires an axios instance into the unauthorized-handler chain above. Any lib that creates
 * its own axios.create() client must call this (interceptors are not inherited from the
 * global axios instance), otherwise its 401s are invisible to dead-session detection.
 */
export const attachUnauthorizedInterceptor = client => {
  client.interceptors.response.use(
    response => response,
    error => {
      unauthorizedHandlers.forEach(handler => handler(error));
      return Promise.reject(error);
    }
  );
};

[defaultClient, noCacheClient, fesClient, tmsClient, temClient, arClient, iocDashboardClient].forEach(
  attachUnauthorizedInterceptor
);

const cachedClients = {};
const getCachedClient = (baseURL, extraHeaders) => {
  const cacheKey = extraHeaders ? `${baseURL}|${JSON.stringify(extraHeaders)}` : baseURL;
  let client = cachedClients[cacheKey];
  if (!client) {
    client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json', ...extraHeaders },
      withCredentials: true
    });
    attachUnauthorizedInterceptor(client);
    cachedClients[cacheKey] = client;
  }
  return client;
};

// basePath should start with a slash, e.g. '/api/v1'
const createRegionalClient = (_region, basePath, extraHeaders) => {
  if (!_region) return getCachedClient(basePath, extraHeaders);
  const region = _region.toLowerCase();
  if (!REGIONS.includes(region)) {
    console.warn(`Region "${region}" is not supported (${REGIONS}). Default "${basePath}" client was used.`);
    return getCachedClient(basePath, extraHeaders);
  }
  return getCachedClient(`/${region}${basePath}`, extraHeaders);
};

export const getRegionPath = (region, path) => {
  if (!path.startsWith('/')) path = `/${path}`;
  return region && REGIONS.includes(region.toLowerCase()) ? `/${region.toLowerCase()}${path}` : path;
};

// Don't forget to add new clients to apps/proxy-api-paths.js as well!
/** === API V1 Client */
export const createApiClient = region => createRegionalClient(region, API_PATHS.API_V1);

/** === Alert Service Client */
export const createAlertClient = region => createRegionalClient(region, API_PATHS.ALERT_SERVICE);

/** === Social Media Fake Pages Client */
export const createSocialMediaClient = region => createRegionalClient(region, API_PATHS.SMFP_SERVICE);

/** === Lookalike Domain Client (Phishint) */
export const createLookAlikeDomainClient = region => createRegionalClient(region, API_PATHS.LOOKALIKE_DOMAIN_SERVICE);

/** === Serializes query params with arrays as REPEATED keys (`?types=domain&types=url`). */
export const serializeRepeatedKeyParams = params => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(item => item != null && searchParams.append(key, item));
      return;
    }
    searchParams.append(key, value);
  });

  return searchParams.toString();
};

/**
 * TIP resolves which tenant a request operates on from this header: the customer id of the tenant
 * selected in the Argos tenant switcher. No header means the login customer's own tenant.
 */
export const TIP_CUSTOMER_ID_HEADER = 'X-Cyberint-Customer-Id';

/**
 * === Threat Intel Platform Client (IOC Management)
 * `customerId` must be passed only while managing another tenant - see TenantUtils.getManagedCustomerId.
 * The identity token is bound to the login host, so these calls must stay same-origin: never point
 * them at a child tenant's subdomain and never proxy them through a backend service token.
 */
export const createTipClient = (region, customerId) => {
  const client = createRegionalClient(
    region,
    API_PATHS.TIP_SERVICE,
    customerId ? { [TIP_CUSTOMER_ID_HEADER]: customerId } : undefined
  );
  client.defaults.paramsSerializer = serializeRepeatedKeyParams;
  return client;
};
